<template>
  <div class="fit-score-list" :style="{ height: fitScoreRowHeight }">
    <div v-if="showAssessmentFit" class="culture-fit fit-score" ref="cultureFit" :style="fitScoreStyle">{{ '-' }}</div>
    <div v-if="showAssessmentFit" class="personality-fit fit-score" ref="personalityFit" :style="fitScoreStyle">{{ '-' }}</div>
    <div v-if="showProblemSolvingFit" class="problem-solving-fit fit-score" ref="problemSolvingFit" :style="fitScoreStyle">{{ '-' }}</div>
    <el-tooltip v-if="showResumeFit" effect="light" popper-class="light-tooltip-popper ai-resume-match-tooltip" placement="left" :content="aiResumeMatchSummary" :disabled="!resumeFitScore || !aiResumeMatchSummary">
      <div class="resume-fit fit-score" ref="resumeFit" :style="fitScoreStyle">{{ '-' }}</div>
    </el-tooltip>
    <el-tooltip v-if="showAIRecommendedFit" effect="light" popper-class="light-tooltip-popper ai-recommended-tooltip" placement="left" :content="aiRecommendedSummary" :disabled="!showAIRecommendedFit || !aiRecommendedFit">
      <div v-if="true" class="ai-recommended-fit fit-score" ref="aiRecommendedFit" :style="fitScoreStyle">{{ '-' }}</div>
    </el-tooltip>
    <div v-if="showAiInterviewFit" class="ai-interview-fit fit-score" ref="aiInterviewFit" :style="fitScoreStyle">{{ '-' }}</div>
    <div v-if="showAIInterviewScore" class="ai-interview-score fit-score" ref="aiInterviewScore" :style="fitScoreStyle">{{ '-' }}</div>
    <RecommendationResultChart
      v-if="showInterviewScore"
      placement="left"
      :decision="interviewRecommendation"
      :score="interviewScore"
      :completion="completion"
      :hmInfoList="hmInfoList"
      :fitScoreStyle="fitScoreStyle"
      @refresh-chart="$emit('refresh-chart')"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { generateFitEchartsOption } from '@/constant/application'
import { getJobFitScoreColor, formatJobFitScore } from '@/utils/common'
import RecommendationResultChart from '@/views/adminNew/components/recommendationResultChart.vue'

export default {
  components: {
    RecommendationResultChart
  },
  props: {
    personalityFinished: {
      default: false
    },
    problemSolvingFinished: {
      default: false
    },
    resumeFitScore: {
      required: false
    },
    cultureFit: {
      required: false
    },
    personalityFit: {
      required: false
    },
    problemSolvingFit: {
      required: false
    },
    fitScoreRowHeight: {
      required: false
    },
    showProblemSolvingFit: {
      default: false
    },
    showAssessmentFit: {
      default: false
    },
    showPersonalityFit: {
      default: false
    },
    showAiInterviewFit: {
      default: false
    },
    aiInterviewFinished: {
      default: false
    },
    aiInterviewFit: {
      required: false
    },
    showResumeFit: {
      default: false
    },
    aiResumeMatchSummary: {
      type: String,
      default: ''
    },
    showAIRecommendedFit: {
      type: Boolean,
      default: false
    },
    aiRecommendedFit: {
      required: false
    },
    aiRecommendedSummary: {
      type: String,
      default: ''
    },
    aiInterviewScore: {
      required: false
    },
    showAIInterviewScore: {
      default: false
    },
    showInterviewScore: {
      default: false
    },
    interviewRecommendation: {
      required: false
    },
    interviewScore: {
      required: false
    },
    completion: {
      type: [Number, String],
      default: null
    },
    hmInfoList: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      fitScoreStyle: {
        height: '48px',
        width: '48px'
      },
      fitChartConfigList: [
        {
          chartElRef: 'resumeFit',
          hasPermission: () => this.showResumeFit,
          getValue: () => this.resumeFitScore,
          getRingColor: getJobFitScoreColor
        },
        {
          chartElRef: 'aiRecommendedFit',
          hasPermission: () => this.showAIRecommendedFit,
          getValue: () => this.aiRecommendedFit,
          getRingColor: getJobFitScoreColor
        },
        {
          chartElRef: 'cultureFit',
          hasPermission: () => this.showAssessmentFit,
          getValue: () => {
            const val = this.cultureFit
            return this.personalityFinished ? (val === null ? null : parseInt(val * 100)) : null
          },
          getRingColor: this.getColor
        },
        {
          chartElRef: 'personalityFit',
          hasPermission: () => this.showAssessmentFit,
          getValue: () => {
            const val = this.personalityFit
            return this.personalityFinished ? (val === null ? null : parseInt(val * 100)) : null
          },
          getRingColor: this.getColor
        },
        {
          chartElRef: 'problemSolvingFit',
          hasPermission: () => this.showProblemSolvingFit,
          getValue: () => {
            const val = this.problemSolvingFit
            return this.problemSolvingFinished ? (val === null ? null : parseInt(val * 100)) : null
          },
          getRingColor: this.getColor
        },
        {
          chartElRef: 'aiInterviewFit',
          hasPermission: () => this.showAiInterviewFit,
          getValue: () => {
            const val = this.aiInterviewFit
            return this.aiInterviewFinished ? (val === null ? null : parseInt(val)) : null
          },
          getRingColor: this.getColor
        },
        {
          chartElRef: 'aiInterviewScore',
          hasPermission: () => this.showAIInterviewScore,
          getValue: () => {
            const val = this.aiInterviewScore
            return val ? parseInt(val) : null
          },
          getRingColor: this.getColor
        }
      ]
    }
  },
  computed: {
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS')
    }
  },
  async mounted () {
    await this.$nextTick()
    // 遍历 list 创建动态 watch
    _.forEach(this.fitChartConfigList, config => {
      this.refreshFitChart(config)
      // watch hasPermission 变化
      this.$watch(config.hasPermission, () => this.refreshFitChart(config))
      // watch value 变化
      this.$watch(config.getValue, () => this.refreshFitChart(config))
    })
  },
  methods: {
    getColor (val) {
      const intVal = parseInt(val || 0)
      let color = 'rgb(226, 118, 118)' // red
      if (intVal > 40 && intVal < 70) {
        color = 'rgb(244, 169, 88)' // orange
      } else if (intVal >= 70) {
        color = 'rgb(81, 191, 130)' // green
      }
      return color
    },
    refreshFitChart (config) {
      const value = config.getValue()
      const hasPermission = config.hasPermission()
      if (!hasPermission || value === null) {
        return
      }
      const refName = config.chartElRef
      const displayValue = value
      let chartValue = value
      if (refName === 'resumeFit' || refName === 'aiRecommendedFit') {
        if (!value) {
          return
        }
        chartValue = formatJobFitScore(value)
      }
      const ringColor = config.getRingColor && config.getRingColor(value)
      const option = generateFitEchartsOption(displayValue, chartValue, ringColor, '18px', '#606266', '#c3c5c9', 5)
      const scoreEl = this.$refs[refName]
      const scoreChart = echarts.getInstanceByDom(scoreEl) || echarts.init(scoreEl)
      scoreChart.setOption(option)
      this.$nextTick(() => {
        this.$emit('refresh-chart')
      })
    }
  }
}
</script>
<style lang='scss' scoped>
.fit-score-list {
  padding: 0 12px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;

  .fit-score {
    margin-left: auto;
    width: 48px;
    height: 48px;
    line-height: 48px;
    text-align: center;
    font-size: 18px;
    color: #777777;

    &:first-child {
      margin-left: 0;
    }
  }
}
</style>

<style lang="scss">
.ai-resume-match-tooltip {
  max-width: 250px !important;
}
</style>
